import { IconButton } from '@1k5-tech-es/grado-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { ReportableQuarter } from '../../api/types';
import { monthName, periodLabel } from '../../lib/format';
import { monthCells, pickerYears, quarterLabel } from './quarters';

const UNAVAILABLE_HINT = 'Sin actividad suficiente en los dos últimos meses de este trimestre';

/** Month grid: the user picks the first month and sees the whole quarter highlighted. */
export function QuarterPicker({
  quarters,
  selected,
  onSelect,
}: {
  quarters: ReportableQuarter[];
  selected: ReportableQuarter;
  onSelect: (quarter: ReportableQuarter) => void;
}) {
  const years = pickerYears(quarters);
  const [year, setYear] = useState(selected.start.year);
  const [hovered, setHovered] = useState<ReportableQuarter | null>(null);
  const cells = monthCells(year, quarters, selected, hovered);
  const yearIndex = years.indexOf(year);
  const spillsOver = selected.end.year > selected.start.year && selected.start.year === year;

  return (
    <div className="w-full max-w-xs">
      <div className="mb-3 flex items-center justify-between">
        <IconButton
          icon={ChevronLeft}
          size="sm"
          label="Año anterior"
          disabled={yearIndex <= 0}
          onClick={() => setYear(years[yearIndex - 1] ?? year)}
        />
        <span className="text-sm font-semibold tabular-nums">{year}</span>
        <IconButton
          icon={ChevronRight}
          size="sm"
          label="Año siguiente"
          disabled={yearIndex === -1 || yearIndex >= years.length - 1}
          onClick={() => setYear(years[yearIndex + 1] ?? year)}
        />
      </div>

      <div className="grid grid-cols-4 gap-1.5" onMouseLeave={() => setHovered(null)}>
        {cells.map((cell) => {
          const quarter = cell.quarter;
          const tone = cell.selected
            ? 'bg-primary text-primary-foreground shadow-sm'
            : cell.inSelectedRange
              ? 'bg-primary-subtle text-primary-text ring-1 ring-primary-border'
              : cell.inHoverRange
                ? 'bg-primary-subtle/60 text-primary-text ring-1 ring-primary-border'
                : quarter
                  ? 'bg-card text-foreground ring-1 ring-border hover:ring-primary-border'
                  : 'cursor-not-allowed bg-muted text-muted-foreground/60';
          return (
            <button
              key={cell.month}
              type="button"
              disabled={!quarter}
              aria-pressed={cell.selected}
              aria-label={quarter ? `Trimestre ${quarterLabel(quarter)}` : `${monthName(cell.month)} ${year}: ${UNAVAILABLE_HINT}`}
              title={quarter ? quarterLabel(quarter) : UNAVAILABLE_HINT}
              onMouseEnter={() => setHovered(quarter)}
              onFocus={() => setHovered(quarter)}
              onBlur={() => setHovered(null)}
              onClick={() => quarter && onSelect(quarter)}
              className={`relative rounded-lg py-2 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${tone}`}
            >
              {monthName(cell.month).slice(0, 3)}
              {quarter?.inProgress && (
                <span
                  className={`absolute top-1 right-1 size-1.5 rounded-full ${cell.selected ? 'bg-warning-subtle' : 'bg-warning'}`}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <Legend className="bg-primary" label="Inicio" />
        <Legend className="bg-primary-subtle ring-1 ring-primary-border" label="Trimestre" />
        <Legend className="size-1.5 rounded-full bg-warning" label="Mes en curso" />
      </div>
      {spillsOver && (
        <p className="mt-2 text-xs text-primary-text">Continúa en {periodLabel(selected.end)} →</p>
      )}
    </div>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block size-2.5 rounded-sm ${className}`} />
      {label}
    </span>
  );
}
